/**
 * ImpersonationView (US-AE11.F3-0039-S03 · @UC9102) — panel CHỈ ĐỌC "xem như user". Render ở composition
 * root khi có token impersonation trong context. CHỨNG MINH "View as user" hoạt động thật (không chỉ
 * banner): fetch GET /v1/me BẰNG token impersonation → hiển thị đúng hồ sơ của target user.
 *
 * Read-only tuyệt đối: panel KHÔNG cung cấp bất kỳ hành động ghi nào (backend chặn mọi mutating; UI cũng
 * không mời gọi). Token thu hồi (thoát/hết hạn) → context clear → panel ẩn; nếu request đang chạy gặp 403
 * (sid-active check) → hiển thị lỗi thay vì dữ liệu cũ. KHÔNG lộ token thô ra UI (C2/C3).
 */
import { useImpersonatedProfile } from "./use-impersonation.js";
import { useImpersonationToken } from "./impersonation-token-context.js";

export function ImpersonationView() {
  const { token } = useImpersonationToken();
  const { data, isLoading, isError } = useImpersonatedProfile(token);

  // Không trong phiên (chưa start / đã thoát) → panel ẩn hoàn toàn.
  if (token === null) return null;

  return (
    <section
      data-testid="impersonation-view"
      aria-labelledby="impersonation-view-heading"
      className="mx-auto my-4 max-w-2xl rounded-lg border border-tone-warning-bd bg-tone-warning-bg p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <h2 id="impersonation-view-heading" className="text-base font-semibold text-tone-warning-fg">
          Màn hình người dùng (chỉ đọc)
        </h2>
        <span className="rounded border border-tone-warning-bd bg-tone-warning-bg px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-tone-warning-fg">
          Read-only
        </span>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Đang tải hồ sơ người dùng…</p>
      ) : null}

      {isError ? (
        <p className="text-sm text-tone-danger-fg" role="alert">
          Không đọc được hồ sơ người dùng. Phiên có thể đã kết thúc hoặc hết hạn.
        </p>
      ) : null}

      {data ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Mã người dùng</dt>
          <dd className="font-mono tabular-nums">{data.id}</dd>
          <dt className="text-muted-foreground">Họ tên</dt>
          <dd>{data.fullName ?? "—"}</dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd>{data.email ?? "—"}</dd>
          <dt className="text-muted-foreground">Đã hoàn tất onboarding</dt>
          <dd>{data.onboardingCompleted ? "Có" : "Chưa"}</dd>
        </dl>
      ) : null}
    </section>
  );
}
